import { forgotPasswordSchema, passwordSchema } from '@bifeai/shared';
import { Button, Callout, Field, Input } from '@bifeai/ui';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

/** Olvidé mi contraseña: se pide el email y llega un link. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá el email.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/forgot', parsed.data);
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="¿Olvidaste la contraseña?" subtitle="Te mandamos un link a tu email para que elijas una nueva.">
      {sent ? (
        <div className="bf-form">
          <Callout tone="success">
            Si <strong>{email}</strong> está registrado, en unos minutos te llega un email con el link. Revisá también la carpeta de spam. El link
            vence en 30 minutos.
          </Callout>
          <p className="bf-hint">
            Si no te llega o no tenés email propio, pedile al dueño de la carnicería que te genere una contraseña nueva desde Usuarios.
          </p>
          <Link to="/ingresar" className="bf-btn bf-btn--block">
            Volver a ingresar
          </Link>
        </div>
      ) : (
        <form className="bf-form" onSubmit={submit} noValidate>
          <Field label="Email" error={error ?? undefined}>
            <Input
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@carniceria.com"
            />
          </Field>
          <Button type="submit" variant="primary" block loading={busy}>
            Mandarme el link
          </Button>
          <Link to="/ingresar" className="bf-hint" style={{ textAlign: 'center' }}>
            Volver a ingresar
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

/** Contraseña nueva con el link del email (el token viene después del #, así no queda en ningún registro). */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.slice(1));
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá la contraseña.');
    if (password !== repeat) return setError('Las dos contraseñas no coinciden.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/reset', { token, newPassword: password });
      // El token ya no sirve: se saca de la barra de direcciones.
      window.history.replaceState(null, '', window.location.pathname);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Link incompleto" subtitle="Abrí el link tal cual te llegó al email, o pedí uno nuevo.">
        <Link to="/recuperar" className="bf-btn bf-btn--primary bf-btn--block">
          Pedir un link nuevo
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Elegí tu contraseña nueva" subtitle="Al cambiarla se cierran tus sesiones abiertas en todos los dispositivos.">
      {done ? (
        <div className="bf-form">
          <Callout tone="success">Listo, ya podés ingresar con tu contraseña nueva.</Callout>
          <Button variant="primary" block onClick={() => navigate('/ingresar', { replace: true })}>
            Ingresar
          </Button>
        </div>
      ) : (
        <form className="bf-form" onSubmit={submit} noValidate>
          {error && (
            <Callout tone="danger">
              {error} {error.includes('venció') && <Link to="/recuperar">Pedir uno nuevo</Link>}
            </Callout>
          )}
          <Field label="Contraseña nueva" hint="Al menos 10 caracteres, con letras y números.">
            <Input type="password" autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label="Repetila">
            <Input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
          </Field>
          <Button type="submit" variant="primary" block loading={busy}>
            Guardar contraseña
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
